import { useState, useEffect, useCallback } from 'react';
import { useCategoryService, Category } from '../services/categoryService';
import { isLandingGearSubAssemblyPoolFolder } from '../utils/landingGearAttachUtils';


export const useWarehouseCategories = (componentType?: string) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { getWarehouseUploadFolders } = useCategoryService();

  const loadFolders = useCallback(async (type: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getWarehouseUploadFolders(type);
      setCategories(data ?? []);
      if (!data) setError('Failed to load warehouse folders');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [getWarehouseUploadFolders]);

  useEffect(() => {
    if (componentType && componentType.length > 0) {
      loadFolders(componentType);
    } else {
      setCategories([]);
    }
  }, [componentType, loadFolders]);

  /** Serial-number folders with no parentCode (top-level) */
  const getTopLevelFolders = useCallback(() =>
    categories.filter((category) => {
      if (category.parentCode) {
        return false;
      }

      const isLandingGear =
        componentType?.localeCompare("landingGear", undefined, {
          sensitivity: "accent",
        }) === 0;

      if (
        isLandingGear &&
        (isLandingGearSubAssemblyPoolFolder(category.code) ||
          isLandingGearSubAssemblyPoolFolder(category.name))
      ) {
        return false;
      }

      return true;
    }),
  [categories, componentType]);

  /** WarehouseCategory children under a given serial folder */
  const getChildrenByParentCode = useCallback((parentCode: string) =>
    categories.filter(c => c.parentCode === parentCode),
  [categories]);

  const formatLabel = useCallback((cat: Category) => cat.name, []);

  return {
    categories,
    loading,
    error,
    getTopLevelFolders,
    getChildrenByParentCode,
    formatLabel,
  };
};
